import axios, { type AxiosInstance } from 'axios'

export interface AuthedClientOptions {
  /** Axios baseURL, z.B. '/api' oder '/api/customer' */
  baseURL: string
  /** localStorage-Key des zustand persist-Stores (enthaelt state.accessToken / state.refreshToken) */
  storageKey: string
  /** Absolute URL des Refresh-Endpoints, z.B. '/api/auth/refresh' */
  refreshUrl: string
  /** Pfad fuer Redirect nach fehlgeschlagenem Refresh */
  loginPath: string
}

interface PersistedAuthState {
  state?: { accessToken?: string; refreshToken?: string }
}

export function createAuthedClient(opts: AuthedClientOptions): AxiosInstance {
  const { baseURL, storageKey, refreshUrl, loginPath } = opts

  const client = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  })

  function readState(): PersistedAuthState['state'] | null {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return null
    try {
      return (JSON.parse(stored) as PersistedAuthState).state ?? null
    } catch {
      return null
    }
  }

  function writeTokens(accessToken: string, refreshToken: string) {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return
    try {
      const parsed = JSON.parse(stored)
      parsed.state = { ...(parsed.state ?? {}), accessToken, refreshToken }
      localStorage.setItem(storageKey, JSON.stringify(parsed))
    } catch { /* ignore */ }
  }

  function redirectToLogin() {
    localStorage.removeItem(storageKey)
    if (typeof window !== 'undefined' && window.location.pathname !== loginPath) {
      window.location.href = loginPath
    }
  }

  client.interceptors.request.use((config) => {
    const state = readState()
    if (state?.accessToken) {
      config.headers.Authorization = `Bearer ${state.accessToken}`
    }
    return config
  })

  let isRefreshing = false
  let subscribers: Array<(token: string) => void> = []

  const notify = (token: string) => {
    subscribers.forEach((cb) => cb(token))
    subscribers = []
  }

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config
      if (!originalRequest || error?.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error)
      }

      const state = readState()
      if (!state?.refreshToken) {
        redirectToLogin()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribers.push((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            originalRequest._retry = true
            client(originalRequest).then(resolve).catch(reject)
          })
        })
      }

      isRefreshing = true
      originalRequest._retry = true

      try {
        const { data } = await axios.post(refreshUrl, { refreshToken: state.refreshToken })
        writeTokens(data.accessToken, data.refreshToken)
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`
        notify(data.accessToken)
        return client(originalRequest)
      } catch (refreshErr) {
        subscribers = []
        redirectToLogin()
        return Promise.reject(refreshErr)
      } finally {
        isRefreshing = false
      }
    },
  )

  return client
}
